import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Youtube, Music2, Instagram, Facebook, Info, XCircle,
  ExternalLink, Clock, Users,
} from 'lucide-react';

// Same illustrative requirement set used on the Monetization Insights page
// — intentionally mirrored here, not imported, so this dialog stays
// self-contained. Hardcoded and unverified; not live platform eligibility.
const PLATFORMS = [
  { key: 'youtube', name: 'YouTube', icon: Youtube, color: 'text-red-500', bg: 'bg-red-500/10', requirements: ['1,000+ Subscribers', '4,000 Watch Hours (12 months)', 'AdSense Account'] },
  { key: 'tiktok', name: 'TikTok', icon: Music2, color: 'text-pink-500', bg: 'bg-pink-500/10', requirements: ['10,000+ Followers', '100,000 Video Views (30 days)', '18+ Years Old'] },
  { key: 'instagram', name: 'Instagram', icon: Instagram, color: 'text-purple-500', bg: 'bg-purple-500/10', requirements: ['Professional Account', 'Meet Partner Eligibility', 'Follow Community Guidelines'] },
  { key: 'facebook', name: 'Facebook', icon: Facebook, color: 'text-blue-500', bg: 'bg-blue-500/10', requirements: ['10,000 Page Followers', '600,000 Minutes Viewed (60 days)', '5+ Active Videos'] },
];

/** Platform Intelligence detail dialog — one tile per platform, real
 * connection status from Creator Profile, everything else (demographics,
 * best content, posting-time suggestions, monetization progress) stays a
 * labeled empty/illustrative state until real platform data is connected. */
export default function PlatformIntelligenceDialog({ open, onOpenChange, connectedPlatforms }) {
  const navigate = useNavigate();
  const [active, setActive] = useState('youtube');
  const platform = PLATFORMS.find(p => p.key === active);
  const isConnected = connectedPlatforms.includes(active);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#14171F] border-white/10 sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="platform-intelligence-dialog">
        <DialogHeader><DialogTitle className="text-white">Platform Intelligence</DialogTitle></DialogHeader>

        <div className="grid grid-cols-4 gap-2">
          {PLATFORMS.map(p => (
            <button
              key={p.key}
              onClick={() => setActive(p.key)}
              className={`flex flex-col items-center gap-1.5 rounded-lg border p-3 transition-colors ${active === p.key ? 'border-[#3A7BFF] bg-[#3A7BFF]/15' : 'border-white/10 hover:bg-white/5'}`}
              data-testid={`platform-tile-${p.key}`}
            >
              <p.icon className={`h-4 w-4 ${p.color}`} />
              <span className="text-[11px] text-white">{p.name}</span>
              <Badge variant="outline" className={`text-[9px] px-1 py-0 ${connectedPlatforms.includes(p.key) ? 'border-green-500/30 text-green-400' : 'border-white/10 text-[#8A93A6]'}`}>
                {connectedPlatforms.includes(p.key) ? 'Connected' : 'Not Connected'}
              </Badge>
            </button>
          ))}
        </div>

        <div className="space-y-4" data-testid={`platform-detail-${active}`}>
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-lg ${platform.bg}`}><platform.icon className={`h-5 w-5 ${platform.color}`} /></div>
            <p className="text-white font-medium">{platform.name}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Card className="glass border-white/5"><CardContent className="pt-4 pb-4"><p className="text-xs text-[#8A93A6] mb-1 flex items-center gap-1.5"><Users className="h-3 w-3" />Audience Demographics</p><p className="text-xs text-[#555E6E]">{isConnected ? 'No demographic data synced yet.' : 'Connect this platform to see audience demographics.'}</p></CardContent></Card>
            <Card className="glass border-white/5"><CardContent className="pt-4 pb-4"><p className="text-xs text-[#8A93A6] mb-1">Best-Performing Content</p><p className="text-xs text-[#555E6E]">No published content tracked yet.</p></CardContent></Card>
            <Card className="glass border-white/5"><CardContent className="pt-4 pb-4"><p className="text-xs text-[#8A93A6] mb-1 flex items-center gap-1.5"><Clock className="h-3 w-3" />Recommended Posting Times</p><p className="text-xs text-[#C0C7D4]">Evenings, 7–9 PM <span className="text-[#555E6E]">(demo recommendation)</span></p></CardContent></Card>
            <Card className="glass border-white/5"><CardContent className="pt-4 pb-4"><p className="text-xs text-[#8A93A6] mb-1">Content Recommendations</p><p className="text-xs text-[#C0C7D4]">Short, high-retention openers tend to perform well here <span className="text-[#555E6E]">(demo)</span></p></CardContent></Card>
          </div>

          <Card className="glass border-white/5">
            <CardContent className="pt-4 pb-4 space-y-2.5">
              <div className="flex items-center justify-between">
                <p className="text-sm text-white font-medium">Monetization Eligibility &amp; Progress</p>
                <Badge variant="outline" className="border-yellow-500/30 text-yellow-400 text-[10px]">Illustrative</Badge>
              </div>
              <ul className="space-y-1.5">
                {platform.requirements.map((r) => (
                  <li key={r} className="flex items-center gap-2 text-xs text-[#8A93A6]"><XCircle className="h-3.5 w-3.5 text-gray-500 shrink-0" />{r}</li>
                ))}
              </ul>
              <Alert className="bg-yellow-500/10 border-yellow-500/30">
                <Info className="h-3.5 w-3.5 text-yellow-500" />
                <AlertDescription className="text-[11px] text-yellow-400">
                  These requirements are hardcoded examples, not verified current platform rules or confirmed eligibility.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          <Button variant="outline" className="border-white/10 w-full" onClick={() => navigate('/app/analyze/monetization-insights')} data-testid="platform-view-monetization-btn">
            <ExternalLink className="h-4 w-4 mr-2" />
            View Full Monetization Insights
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
